/**
 * Macro #25 — Owner/Creator Analytics V1 API client (owner-facing, READ-ONLY).
 *
 * Server-authoritative, wallet-scoped aggregation over canonical state. The
 * frontend never computes business truth and never sends a wallet — the session
 * cookie is the sole authority. GET only; there is no analytics mutation.
 */
import { API_BASE } from "./api";

export class AnalyticsError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "AnalyticsError";
    this.status = status;
    this.code = code;
  }
}

export type Range = "7d" | "30d" | "90d" | "all";
export type Series = "referrals" | "rewards" | "influence";

export interface AnalyticsSelf {
  wallet: string;
  micro_per_usdc: number;
  generated_at: string;
  current: {
    parcels_owned: number;
    total_area: number;
    owned_territories: number;
    published_territories: number;
    created_artworks: number;
    placed_artworks: number;
  };
  lifetime: {
    primary_acquisitions: number;
    primary_acquisition_volume_micro: number;
    primary_area_acquired: number;
    qualified_referrals: number;
    pending_referrals: number;
  };
  referral: { code: string | null; enabled: boolean; qualified: number; pending: number };
  rewards:
    | { live: false }
    | {
        live: true;
        total_qualified_micro: number;
        referral_total_micro: number;
        neighbor_total_micro: number;
        pending_funding_micro: number;
        available_micro: number;
        withdrawn_gross_micro: number;
      };
  influence:
    | { live: false }
    | { live: true; influence_points: number; badge: string | null; next_badge: string | null; points_to_next: number; progress_pct: number };
  available_ranges: Range[];
  series: Series[];
  unavailable: Array<{ metric: string; reason: string }>;
}

export interface TimeseriesPoint {
  t: string;
  v?: number;
  referral_micro?: number;
  neighbor_micro?: number;
}

export interface Timeseries {
  wallet: string;
  range: Range;
  series: Series;
  time_source: string;
  live: boolean;
  points: TimeseriesPoint[];
  total?: number;
  kind?: string;
}

interface ErrorEnvelope {
  code?: string;
  message?: string;
  detail?: { code?: string; message?: string };
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    throw new AnalyticsError(resp.status, env.code ?? env.detail?.code ?? "UNKNOWN", env.message ?? resp.statusText);
  }
  return body as T;
}

export const analyticsApi = {
  self: (signal?: AbortSignal) => get<AnalyticsSelf>("/api/v1/analytics/self", signal),
  timeseries: (range: Range, series: Series, signal?: AbortSignal) =>
    get<Timeseries>(
      `/api/v1/analytics/self/timeseries?range=${encodeURIComponent(range)}&series=${encodeURIComponent(series)}`,
      signal,
    ),
};
